/**
 * screens/settings/plan.tsx — Plan & Usage.
 * One embedded plan card per product the org has (CC-01). Upgrade, downgrade,
 * top-up and upgrade requests all happen in the billing drawer the card opens —
 * the single source of that UI for every SK product, this console included.
 *
 * Deep link (B9): `?billing_request=<id>&billing_product=<code>` opens the admin request inbox.
 */

import React, { useEffect, useState } from 'react';
import { View, ScrollView, ActivityIndicator } from 'react-native';

import { useTheme } from '../../context/themeContext';
import { SettingsHeader, Panel } from '../../components/platform/primitives';
import { PEmpty } from '../../components/platform/controls';
import { PlanCard, openBillingDrawer } from '../../components/platform/billingEmbed';
import { getCurrentUser } from '../../services/auth/auth';
import { subscribedProductsFrom, productDisplayName } from '../../navigation/planGate';
import { readBillingRequestLink } from '../../navigation/linking';

const PlanScreen: React.FC = () => {
  const { theme, isDark } = useTheme();
  const [products, setProducts] = useState<string[] | null>(null);

  useEffect(() => {
    getCurrentUser()
      .then((me) => setProducts(subscribedProductsFrom(me) ?? []))
      .catch(() => setProducts([]));
  }, []);

  useEffect(() => {
    const link = readBillingRequestLink();
    if (!link) return;
    openBillingDrawer(link.product, isDark, 'upgrade', { request: link.request }).catch(() => undefined);
    // Consumed once; a reload must not reopen the inbox.
    window.history.replaceState(null, '', window.location.pathname);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <ScrollView contentContainerStyle={{ padding: 28, maxWidth: 900, width: '100%', alignSelf: 'center' }}>
      <SettingsHeader title="Plan & Usage" sub="Your plan, usage and wallet for each Softknack product." />
      {products === null ? (
        <ActivityIndicator color={theme.colors.accent} />
      ) : products.length === 0 ? (
        <PEmpty icon="star" title="No products yet">Products your organisation uses appear here with their plan and usage.</PEmpty>
      ) : (
        <View>
          {products.map((p) => (
            <Panel key={p} title={productDisplayName(p)}>
              <PlanCard product={p} />
            </Panel>
          ))}
        </View>
      )}
    </ScrollView>
  );
};

export default PlanScreen;
